import React from 'react'
import { withRouter } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import { Button, Checkbox, Col, Form, Icon, Input, Layout, PageHeader, Row, Select, Spin } from 'antd'
import moment from 'moment'
import CancellationTable from './Table'
import CancellationRequestMessages from '@components/Popup/CancellationRequestMessages'
import GenerateCancellationReport from '@components/Popup/GenerateCancellationReport'
import NewCommissionFromCancellation from '@components/Popup/NewCommission/FromCancellationRequest'
import users from '@services/users'
import { cancellationRequests, files } from '@services/api'
import download from '@utils/download'
import { alertPopup } from '@utils/popup'
import { CANCELLATION_REASON_TYPES, CANCELLATION_STATUS_FILTER_LABELS } from '@texts/cancellation'
import { fileExt } from '@texts/files'
import { filterBar, filterActions } from './style.scss'
import { EXPLAINED_FILTER_ALL, explainedFilterStatus, explainedFilterValue } from './Table/utils'

const { Item: FormItem } = Form

const SEARCH_BY_OPTIONS = {
    requested_for: 'מזהה לקוח',
    created_by: 'מגיש הבקשה',
    agent_id: 'מזהה סוכן',
    parent_agency_id: 'סוכן אב',
    request_id: 'מזהה בקשה'
}

const RTL_DROPDOWN = { direction: 'rtl', textAlign: 'right' } as React.CSSProperties

// Payment flag filters: page state key -> store filter key. They search across every status and are
// mutually exclusive, so at most one is ever on.
const PAYMENT_FILTERS = { filterPaymentRequired: 'is_payment_required', filterPaid: 'paid' }

@inject((store: any) => {
    const { popupStore, authStore, cancellationRequestStore, subscriptionsStore } = store
    return {
        popupStore,
        authStore,
        cancellationRequestStore,
        subscriptionsStore
    }
})
@observer
class CancelSubRequest extends React.Component<any, any> {
    constructor(props) {
        super(props)
        this.state = {
            requests: [],
            recipients: [],
            searchText: '',
            searchBy: 'requested_for',
            filterStatus: ['OPEN'],
            filterReason: [],
            filterLegal: [],
            filterPaymentRequired: false,
            filterPaid: false,
            filterExplained: EXPLAINED_FILTER_ALL,
            clearedDefaultStatus: false,
            topLoading: false,
            exporting: false
        }
    }

    componentDidMount(): void {
        const {
            match: { params },
            subscriptionsStore: { getSubscriptions, subscriptions },
            cancellationRequestStore: { resetSearch, setFilters, resetFilters }
        } = this.props
        resetSearch()
        // The store outlives the page, but the constructor state starts at the defaults: reset the
        // store too, so the controls always show the filters actually sent.
        resetFilters()

        if (params.id) {
            setFilters()
            this.setState(
                {
                    searchBy: 'request_id',
                    searchText: params.id,
                    filterStatus: [],
                    filterReason: [],
                    filterLegal: [],
                    filterPaymentRequired: false,
                    filterPaid: false,
                    filterExplained: EXPLAINED_FILTER_ALL,
                    clearedDefaultStatus: false
                },
                () => {
                    this.search(null)
                }
            )
        } else {
            // this.setState({ searchBy: 'status', searchText: 'OPEN' }, () => {
            //     this.search(null)
            // })
            this.getRequests()
        }

        this.getAdminList()

        //subscriptions are needed when creating new commission:
        if (subscriptions.length === 0) {
            getSubscriptions()
        }
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
        const {
            match: { params },
            cancellationRequestStore: { setFilters, resetFilters, resetSearch }
        } = this.props
        if (prevProps.match.params.id !== params.id) {
            if (params.id) {
                setFilters()
                this.setState(
                    {
                        searchBy: 'request_id',
                        searchText: params.id,
                        filterStatus: [],
                        filterReason: [],
                        filterLegal: [],
                        filterPaymentRequired: false,
                        filterPaid: false,
                        filterExplained: EXPLAINED_FILTER_ALL,
                        clearedDefaultStatus: false
                    },
                    () => this.search(null)
                )
            } else {
                // id was cleared (navigated back to /cancelSubRequests) — reset to defaults and refresh.
                this.setState(
                    {
                        searchBy: 'requested_for',
                        searchText: '',
                        filterStatus: ['OPEN'],
                        filterReason: [],
                        filterLegal: [],
                        filterPaymentRequired: false,
                        filterPaid: false,
                        filterExplained: EXPLAINED_FILTER_ALL,
                        clearedDefaultStatus: false
                    },
                    () => {
                        resetFilters()
                        resetSearch()
                        this.getRequests()
                    }
                )
            }
        }
    }

    getAdminList = () => {
        users
            .getUsersList()
            .then(res => this.setState({ recipients: res.data }))
            .catch(error => console.error(error))
    }

    getRequests = () => {
        const {
            cancellationRequestStore: { getCancellationRequests, list }
        } = this.props

        const withLoading = list.length <= 0
        this.setState({ topLoading: true }, () => {
            getCancellationRequests({ withLoading })
                .then(() => this.setState({ topLoading: false }))
                .catch(() => this.setState({ topLoading: false }))
        })
    }

    handleInput = e => {
        this.setState({ searchText: e.target.value })
    }

    search = async e => {
        e && e.preventDefault()
        const {
            cancellationRequestStore: { setSearch, resetSearch, getCancellationRequests }
        } = this.props
        const { searchBy, searchText } = this.state
        if (searchBy && searchText) {
            await setSearch(searchBy, searchText)
        } else {
            await resetSearch()
        }

        // @@@@@@
        // getCancellationRequests()
        this.getRequests()
    }

    handleFilterChange = (stateKey, storeKey, values) => {
        const {
            cancellationRequestStore: { setFilters }
        } = this.props
        this.setState({ [stateKey]: values }, () => {
            setFilters({ [storeKey]: values })
            this.getRequests()
        })
    }

    // "הוסבר ללקוח": כן / לא empty the status filter (every status); הכל (or clearing the dropdown) puts back the
    // default OPEN. Either way the payment filters no longer own the status, so they won't restore OPEN later.
    // setFilters goes back to page 1.
    handleExplainedFilter = selected => {
        const {
            cancellationRequestStore: { setFilters }
        } = this.props
        const filterExplained = selected === 1 || selected === 0 ? selected : EXPLAINED_FILTER_ALL
        const filterStatus = explainedFilterStatus(filterExplained)
        this.setState({ filterExplained, filterStatus, clearedDefaultStatus: false }, () => {
            setFilters({ is_cancel_explained: explainedFilterValue(filterExplained), status: filterStatus })
            this.getRequests()
        })
    }

    // Turning a payment filter on turns the other one off. The default OPEN status would hide matching
    // rows in every other status, so turning one on drops it; any other status selection is deliberate
    // and kept. When the filter goes off, only the OPEN this handler removed is put back, if no status
    // was picked since.
    handlePaymentFilter = (stateKey, checked) => {
        const {
            cancellationRequestStore: { setFilters }
        } = this.props
        const { filterStatus, clearedDefaultStatus } = this.state
        const isDefaultStatus = filterStatus.length === 1 && filterStatus[0] === 'OPEN'
        const clearStatus = checked && isDefaultStatus
        const restoreStatus = !checked && clearedDefaultStatus && filterStatus.length === 0
        const nextStatus = clearStatus ? [] : restoreStatus ? ['OPEN'] : filterStatus

        const flags = {}
        const storeFlags = {}
        Object.keys(PAYMENT_FILTERS).forEach(key => {
            const on = key === stateKey && checked
            flags[key] = on
            storeFlags[PAYMENT_FILTERS[key]] = on || null
        })

        this.setState(
            {
                ...flags,
                filterStatus: nextStatus,
                clearedDefaultStatus: clearStatus || (checked && clearedDefaultStatus)
            },
            () => {
                setFilters({ ...storeFlags, status: nextStatus })
                this.getRequests()
            }
        )
    }

    resetAllFilters = async () => {
        const {
            cancellationRequestStore: { resetSearch, resetFilters, getCancellationRequests }
        } = this.props

        this.props.history.push('/cancelSubRequests')

        this.setState({
            filterStatus: ['OPEN'],
            filterReason: [],
            filterLegal: [],
            filterPaymentRequired: false,
            filterPaid: false,
            filterExplained: EXPLAINED_FILTER_ALL,
            clearedDefaultStatus: false,
            searchBy: 'requested_for',
            searchText: ''
        })

        await resetFilters()
        await resetSearch()
        // @@@@@@
        // getCancellationRequests()
        this.getRequests()
    }

    async downloadFile(file) {
        let downloadedFile = await files
            .assert(file.file_url)
            .then(res => new Blob([res.data], { type: fileExt[file.file_ext] }))
        const fileName = `דוח בקשות ביטול ${moment().format('DD-MM-YYYY')}.xlsx`
        download(downloadedFile, fileName)
    }

    exportRows = () => {
        const { cancellationRequestStore } = this.props
        this.setState({ exporting: true }, () => {
            cancellationRequests
                .exportCancellationRequests({ filterArray: cancellationRequestStore.buildFilterArray() })
                .then(res => {
                    if (res.data && res.data.empty) {
                        alertPopup({ type: 'alert', title: 'אין נתונים להורדה', body: '' })
                        return
                    }
                    return this.downloadFile(res.data)
                })
                .catch(() => alertPopup({ type: 'error', title: 'אירעה שגיאה', body: '' }))
                .then(() => this.setState({ exporting: false }))
        })
    }

    render() {
        const {
            cancellationRequestStore: { list, loadingCancellationRequests },
            authStore: { roles }
        } = this.props
        const {
            filterStatus,
            filterReason,
            filterLegal,
            filterPaymentRequired,
            filterPaid,
            filterExplained,
            searchBy,
            searchText,
            recipients,
            topLoading,
            exporting
        } = this.state
        const safeRoles = roles || []

        const canGenerateReport =
            safeRoles.includes('admin') || safeRoles.includes('moderator') || safeRoles.includes('money')

        return (
            <Layout>
                <PageHeader
                    onBack={() => {
                        this.props.history.push('/')
                    }}
                    title="ניהול בקשות ביטול"
                    extra={
                        canGenerateReport
                            ? [
                                  <Button
                                      key="report"
                                      // same as the sto-search header buttons: pin to the left corner,
                                      // otherwise `extra` sticks to the RTL title on the right.
                                      style={{ position: 'absolute', left: '24px' }}
                                      onClick={() => this.props.popupStore.togglePopup('GenerateCancellationReport')}
                                  >
                                      <Icon type="file-excel" />
                                      <span>דו״ח ביטולים</span>
                                  </Button>,
                                  <Button
                                      key="export"
                                      style={{ position: 'absolute', left: '165px' }}
                                      disabled={exporting}
                                      onClick={this.exportRows}
                                  >
                                      <Icon type="file-excel" />
                                      <span>הורדת קובץ</span>
                                      {exporting && <Spin size="small" style={{ marginRight: '8px' }} />}
                                  </Button>
                              ]
                            : []
                    }
                >
                    <Form onSubmit={this.search} layout="vertical" className={filterBar}>
                        <Row gutter={16} type="flex" justify="start">
                            <Col xs={12} md={5} lg={3}>
                                <FormItem label="חיפוש לפי">
                                    <Select
                                        style={{ width: '100%' }}
                                        value={searchBy}
                                        dropdownStyle={RTL_DROPDOWN}
                                        onChange={value => this.setState({ searchBy: value })}
                                    >
                                        {Object.keys(SEARCH_BY_OPTIONS).map(key => (
                                            <Select.Option key={key} value={key}>
                                                {SEARCH_BY_OPTIONS[key]}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </FormItem>
                            </Col>
                            <Col xs={12} md={4} lg={2}>
                                <FormItem label="ערך לחיפוש">
                                    {/* 80px ≈ 7 digits plus antd's 11px side padding */}
                                    <Input
                                        value={searchText}
                                        onChange={this.handleInput}
                                        style={{ width: '100%', minWidth: '100%' }}
                                    />
                                </FormItem>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <FormItem label="סטטוס">
                                    <Select
                                        mode="multiple"
                                        maxTagCount={1}
                                        allowClear
                                        placeholder="כל הסטטוסים"
                                        style={{ width: '100%' }}
                                        dropdownStyle={RTL_DROPDOWN}
                                        value={filterStatus}
                                        onChange={values => this.handleFilterChange('filterStatus', 'status', values)}
                                    >
                                        {Object.keys(CANCELLATION_STATUS_FILTER_LABELS).map(key => (
                                            <Select.Option key={key} value={key}>
                                                {CANCELLATION_STATUS_FILTER_LABELS[key]}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </FormItem>
                            </Col>
                            <Col xs={12} md={6} lg={5}>
                                <FormItem label="סיבת הבקשה">
                                    <Select
                                        mode="multiple"
                                        maxTagCount={1}
                                        allowClear
                                        placeholder="כל הסיבות"
                                        style={{ width: '100%' }}
                                        dropdownStyle={RTL_DROPDOWN}
                                        value={filterReason}
                                        onChange={values =>
                                            this.handleFilterChange('filterReason', 'request_reason', values)
                                        }
                                    >
                                        {Object.keys(CANCELLATION_REASON_TYPES).map(key => (
                                            <Select.Option key={key} value={key}>
                                                {CANCELLATION_REASON_TYPES[key]}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </FormItem>
                            </Col>
                            <Col xs={12} md={5} lg={3}>
                                <FormItem label="הליך משפטי">
                                    <Select
                                        mode="multiple"
                                        maxTagCount={1}
                                        allowClear
                                        placeholder="הכל"
                                        style={{ width: '100%' }}
                                        dropdownStyle={RTL_DROPDOWN}
                                        value={filterLegal}
                                        onChange={values =>
                                            this.handleFilterChange('filterLegal', 'in_legal_proceedings', values)
                                        }
                                    >
                                        <Select.Option value={1}>כן</Select.Option>
                                        <Select.Option value={0}>לא</Select.Option>
                                    </Select>
                                </FormItem>
                            </Col>
                            <Col xs={12} md={5} lg={3}>
                                <FormItem label="הוסבר ללקוח">
                                    <Select
                                        allowClear
                                        placeholder="הכל"
                                        style={{ width: '100%' }}
                                        dropdownStyle={RTL_DROPDOWN}
                                        value={filterExplained}
                                        onChange={this.handleExplainedFilter}
                                    >
                                        <Select.Option value={EXPLAINED_FILTER_ALL}>הכל</Select.Option>
                                        <Select.Option value={1}>כן</Select.Option>
                                        <Select.Option value={0}>לא</Select.Option>
                                    </Select>
                                </FormItem>
                            </Col>
                            <Col xs={12} md={4} lg={2}>
                                <FormItem label="נדרש תשלום">
                                    <Checkbox
                                        checked={filterPaymentRequired}
                                        onChange={e =>
                                            this.handlePaymentFilter('filterPaymentRequired', e.target.checked)
                                        }
                                    />
                                </FormItem>
                            </Col>
                            <Col xs={12} md={4} lg={2}>
                                <FormItem label="שולם">
                                    <Checkbox
                                        checked={filterPaid}
                                        onChange={e => this.handlePaymentFilter('filterPaid', e.target.checked)}
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24} className={filterActions}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={topLoading}
                                    style={{ margin: 'unset' }}
                                >
                                    חיפוש
                                </Button>
                                <Button onClick={this.resetAllFilters} style={{ margin: 'unset' }}>
                                    איפוס
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                    {!loadingCancellationRequests ? (
                        <CancellationTable list={list} recipients={recipients} />
                    ) : (
                        <Spin tip="טוען" style={{ display: 'block', textAlign: 'center', padding: 40 }} />
                    )}
                </PageHeader>
                <CancellationRequestMessages />
                <GenerateCancellationReport />
                {this.props.popupStore.popupType === 'newCommissionFromCancellation' ? (
                    <NewCommissionFromCancellation />
                ) : (
                    ''
                )}
            </Layout>
        )
    }
}

export default withRouter(CancelSubRequest)
